import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LOGO_URL = "/images/logo.png";

/** Below this width: compact bar (logo + title + menu only); nav + CTAs live in the drawer. */
const MOBILE_HEADER_BREAKPOINT_PX = 1024;

const styles = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(12px)",
    borderBottom: "1px solid #e2e8f0",
  },
  navContainer: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "24px",
  },

  brand: { display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" },
  logoImg: { height: "46px", width: "auto", display: "block" },

  // ✅ small stability tweaks (keeps spacing consistent across pages)
  brandTextPrimary: { fontWeight: 700, fontSize: "20px", color: "#0b1530", lineHeight: 1.15 },
  brandTextSub: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#64748b",
    marginTop: "1px", // was -4px (too tight on some pages)
    lineHeight: 1.2,
  },

  menu: {
    display: "flex",
    gap: "20px",
    fontSize: "14px",
    color: "#64748b",
    fontWeight: 500,
    alignItems: "center",
  },
  menuBtn: {
    cursor: "pointer",
    transition: "color 0.2s",
    whiteSpace: "nowrap",
    border: "none",
    background: "transparent",
    padding: 0,
    color: "inherit",
    font: "inherit",
  },

  ctaGroup: { display: "flex", gap: "10px", alignItems: "center", flexShrink: 0 },
  btn: {
    fontSize: "13px",
    padding: "9px 18px",
    borderRadius: "999px",
    border: "1px solid #d0d7e2",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: 600,
    transition: "all 0.2s ease",
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #1d4ed8, #0b1530)",
    color: "#ffffff",
    borderColor: "transparent",
    boxShadow: "0 10px 25px rgba(15,23,42,0.18)",
  },
  btnGhost: { background: "rgba(15,23,42,0.02)" },

  // Dropdown
  dropWrap: { position: "relative" },
  dropBtn: { display: "inline-flex", alignItems: "center", gap: "6px" },
  caret: { fontSize: "12px", marginTop: "1px" },
  dropMenu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    left: 0,
    minWidth: "220px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    boxShadow: "0 22px 60px rgba(15,23,42,0.16)",
    padding: "8px",
    zIndex: 110, // ✅ ensure it’s clickable above nearby content
  },
  dropItem: {
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    padding: "10px 10px",
    borderRadius: "10px",
    cursor: "pointer",
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dropSub: { fontSize: "12px", color: "#64748b", fontWeight: 500, marginTop: "2px" },
  badge: {
    fontSize: "11px",
    padding: "3px 8px",
    borderRadius: "999px",
    background: "rgba(29,78,216,0.10)",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  disabled: { opacity: 0.55, cursor: "not-allowed" },

  // Mobile
  mobileBtn: {
    display: "none",
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    background: "rgba(15,23,42,0.02)",
    cursor: "pointer",
    fontSize: "18px",
    alignItems: "center",
    justifyContent: "center",
  },
  mobilePanel: {
    borderTop: "1px solid #e2e8f0",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(12px)",
  },
  mobileInner: { maxWidth: "1100px", margin: "0 auto", padding: "12px 20px" },
  mobileList: { display: "flex", flexDirection: "column", gap: "10px", padding: "10px 0" },
  mobileLink: {
    textAlign: "left",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "12px 12px",
    borderRadius: "14px",
    cursor: "pointer",
    fontWeight: 700,
    color: "#0f172a",
  },
  mobileSub: { marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" },

  brandTextWrap: { minWidth: 0, flex: 1 },
  brandTextPrimaryMobile: {
    fontSize: "17px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  mobileCtas: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    paddingBottom: "12px",
    borderBottom: "1px solid #e2e8f0",
    marginBottom: "4px",
  },
};

export default function PublicHeader() {
  const navigate = useNavigate();
  const location = useLocation();

  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_HEADER_BREAKPOINT_PX
  );

  const wrapRef = useRef(null);

  // ✅ new: close-delay timer so moving from button → menu doesn’t instantly close
  const closeTimerRef = useRef(null);
  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const openDropdown = () => {
    clearCloseTimer();
    setDropOpen(true);
  };
  const scheduleCloseDropdown = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setDropOpen(false), 220);
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_HEADER_BREAKPOINT_PX);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setDropOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDown = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  const go = (path) => navigate(path);

  const TutoringDropdown = () => (
    <div
      ref={wrapRef}
      style={styles.dropWrap}
      onMouseEnter={() => !isMobile && openDropdown()}
      onMouseLeave={() => !isMobile && scheduleCloseDropdown()}
    >
      <button
        type="button"
        style={{ ...styles.menuBtn, ...styles.dropBtn }}
        onClick={() => (isMobile ? setDropOpen((v) => !v) : go("/tutoring"))}
        aria-haspopup="menu"
        aria-expanded={dropOpen}
      >
        Tutoring <span style={styles.caret}>▾</span>
      </button>

      {dropOpen && (
        <div
          style={styles.dropMenu}
          role="menu"
          aria-label="Tutoring menu"
          // ✅ key part: keep open while hovering the submenu itself
          onMouseEnter={() => !isMobile && openDropdown()}
          onMouseLeave={() => !isMobile && scheduleCloseDropdown()}
        >
          <button
            type="button"
            style={styles.dropItem}
            onClick={() => {
              setDropOpen(false);
              go("/tutoring");
            }}
          >
            <div>
              Tutoring (Main)
              <div style={styles.dropSub}>Overview of all programs</div>
            </div>
          </button>

          <button
            type="button"
            style={styles.dropItem}
            onClick={() => {
              setDropOpen(false);
              go("/tutoring/sat");
            }}
          >
            <div>
              SAT Tutoring
              <div style={styles.dropSub}>Our flagship program</div>
            </div>
          </button>

          <button type="button" style={{ ...styles.dropItem, ...styles.disabled }} disabled>
            <div>
              AP Tutoring
              <div style={styles.dropSub}>Coming next</div>
            </div>
            <span style={styles.badge}>Coming soon</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <header style={styles.header}>
      <div
        style={{
          ...styles.navContainer,
          ...(isMobile ? { columnGap: "12px" } : {}),
        }}
      >
        <div style={{ ...styles.brand, ...(isMobile ? { flex: 1, minWidth: 0, gap: "8px" } : {}) }} onClick={() => go("/")}>
          <img
            src={LOGO_URL}
            alt="True Education logo"
            style={{
              ...styles.logoImg,
              ...(isMobile ? { height: "40px", flexShrink: 0 } : {}),
            }}
          />
          <div style={isMobile ? styles.brandTextWrap : undefined}>
            <div
              style={{
                ...styles.brandTextPrimary,
                ...(isMobile ? styles.brandTextPrimaryMobile : {}),
              }}
            >
              True Education Cumming
            </div>
            {!isMobile && (
              <div style={styles.brandTextSub}>SAT · ACT · MATH · READING · WRITING · GPA</div>
            )}
          </div>
        </div>

        {!isMobile && (
          <nav style={styles.menu}>
            <button type="button" style={styles.menuBtn} onClick={() => go("/admissions")}>
              Admissions
            </button>
            <button type="button" style={styles.menuBtn} onClick={() => go("/about")}>
              About
            </button>
            <TutoringDropdown />
            <button type="button" style={styles.menuBtn} onClick={() => {}}>
              Testimonials
            </button>
            <button type="button" style={styles.menuBtn} onClick={() => go("/contact")}>
              Contact
            </button>
          </nav>
        )}

        <div style={{ ...styles.ctaGroup, ...(isMobile ? { flexShrink: 0, gap: "8px" } : {}) }}>
          {!isMobile && (
            <>
              <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={() => go("/login")}>
                Login
              </button>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => go("/signup")}>
                Get Started
              </button>
            </>
          )}

          {isMobile && (
            <button
              type="button"
              style={{ ...styles.mobileBtn, display: "flex" }}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? "✕" : "☰"}
            </button>
          )}
        </div>
      </div>

      {isMobile && mobileOpen && (
        <div style={styles.mobilePanel}>
          <div style={styles.mobileInner}>
            <div style={styles.mobileList}>
              <div style={styles.mobileCtas}>
                <button
                  type="button"
                  style={{
                    ...styles.btn,
                    ...styles.btnPrimary,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    setMobileOpen(false);
                    go("/signup");
                  }}
                >
                  Get Started
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.btn,
                    ...styles.btnGhost,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    setMobileOpen(false);
                    go("/login");
                  }}
                >
                  Login
                </button>
              </div>
              <div style={{ ...styles.brandTextSub, padding: "0 2px 10px", textTransform: "none", letterSpacing: "0.02em" }}>
                SAT · ACT · MATH · READING · WRITING · GPA
              </div>
              <button type="button" style={styles.mobileLink} onClick={() => { setMobileOpen(false); go("/admissions"); }}>
                Admissions
              </button>
              <button type="button" style={styles.mobileLink} onClick={() => { setMobileOpen(false); go("/about"); }}>
                About
              </button>

              <button type="button" style={styles.mobileLink} onClick={() => setDropOpen((v) => !v)}>
                Tutoring ▾
                {dropOpen && (
                  <div style={styles.mobileSub}>
                    <button type="button" style={styles.mobileLink} onClick={() => go("/tutoring")}>
                      Tutoring (Main)
                    </button>
                    <button type="button" style={styles.mobileLink} onClick={() => go("/tutoring/sat")}>
                      SAT Tutoring
                    </button>
                    <button type="button" style={{ ...styles.mobileLink, ...styles.disabled }} disabled>
                      AP Tutoring (Coming soon)
                    </button>
                  </div>
                )}
              </button>

              <button type="button" style={styles.mobileLink} onClick={() => {}}>
                Testimonials
              </button>
              <button type="button" style={styles.mobileLink} onClick={() => { setMobileOpen(false); go("/contact"); }}>
                Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
